import { useState, useEffect, useRef, useCallback, useId } from 'react'
import EditDocumentsModal from './EditDocumentsModal'
import type {
  ClientBranding,
  FinTechStack,
  AppVariant,
  GenerateInsideOutPayload,
  RunInsideOutStagePayload,
  Settings,
} from '@shared/types'
import { STAGE_DEPENDENCIES, getTransitiveDependents, formatStageList, type StageIndex } from '@tn/shared'
import { useApi } from '../contexts/ApiContext'
import { useGenerationContext } from '../contexts/GenerationContext'
import { useDropdownOptions } from '../utils/useDropdownOptions'

// ─── Types ────────────────────────────────────────────────────────────────────

type LocalStageStatus = 'idle' | 'running' | 'completed' | 'failed'

interface LocalStageState {
  status: LocalStageStatus
  error?: string
}

export interface PipelineWorkflowProps {
  projectId: string
  clientName?: string
  industry?: string
  functionScope?: string[]
  techStack?: FinTechStack
  variant?: AppVariant
  inputFiles?: Array<{ name: string; base64?: string }>
  branding: ClientBranding
  mmsId?: string
  purpose?: string
  onDone?: () => void
  onEditDocuments?: (updatedFiles: Array<{ base64: string; name: string }>) => void
  onUpdateDocuments?: () => void
  onCancel?: () => void
}

// ─── Stage definitions ────────────────────────────────────────────────────────

const STAGES: Array<{ id: StageIndex; title: string }> = [
  { id: 1, title: 'Stage 1: Org Structure & Data Gaps' },
  { id: 2, title: 'Stage 2: Role Detail & Capacity' },
  { id: 3, title: 'Stage 3: Future Workforce Archetypes' },
  { id: 4, title: 'Stage 4: Process Redesign Roadmap' },
  { id: 5, title: 'Stage 5: Automation Build Portfolio' },
  { id: 6, title: 'Stage 6: Narrative Synthesis' },
]

// Must match the message the job managers and GenerationContext assign on cancel.
const CANCELLED_MESSAGE = 'Cancelled by user.'

const makeInitialStages = (): LocalStageState[] =>
  STAGES.map(() => ({ status: 'idle' }))

// ─── Icons ────────────────────────────────────────────────────────────────────

function FileIcon({ ext }: { ext: string }) {
  const color =
    ext === 'pdf' ? 'text-red-500' :
    ext === 'xlsx' || ext === 'xls' ? 'text-green-600' :
    'text-blue-500'
  return (
    <svg className={`w-4 h-4 flex-shrink-0 ${color}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
    </svg>
  )
}

function RefreshIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23,4 23,10 17,10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  )
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: LocalStageStatus }) {
  if (status === 'completed') {
    return (
      <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold bg-green-500 text-white">
        Completed
      </span>
    )
  }
  if (status === 'running') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
        <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
        Running
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        Failed
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-400">
      Idle
    </span>
  )
}

// ─── Stage card ───────────────────────────────────────────────────────────────

interface StageCardProps {
  stage: typeof STAGES[number]
  state: LocalStageState
  isLast: boolean
  isLocked: boolean    // prerequisite not met → gray card + lock icon
  isDisabled: boolean  // button non-interactive (locked OR job running OR stage running)
  isAnyStageRunning: boolean  // any stage (including other stages) is currently running
  isRunAllActive: boolean  // "Run All" job in flight — hide per-stage cancel
  supplementaryFiles: Array<{ base64: string; name: string }>
  clientName: string
  onRun: () => void
  onCancel: () => void
  onAttachFiles: (files: Array<{ base64: string; name: string }>) => void
  onRemoveFile: (index: number) => void
  onDownloadOutput: (format: 'xlsx' | 'pdf', filename: string) => void
}

function StageCard({ stage, state, isLast, isLocked, isDisabled, isAnyStageRunning, isRunAllActive, supplementaryFiles, clientName, onRun, onCancel, onAttachFiles, onRemoveFile, onDownloadOutput }: StageCardProps) {
  const isCompleted = state.status === 'completed'
  const isRunning = state.status === 'running'
  const isFailed = state.status === 'failed'
  const disabled = isDisabled
  const safeClient = clientName.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'Client'
  // The card is a group named by its title, so assistive tech and UI tests can find one stage's card.
  const titleId = useId()

  return (
    <div className="flex items-stretch flex-shrink-0">
      <div
        role="group"
        aria-labelledby={titleId}
        className={[
          'w-52 rounded-lg border flex flex-col p-4 gap-3 transition-colors',
          isCompleted ? 'bg-green-50 border-green-200' :
          isFailed    ? 'bg-red-50 border-red-200' :
          isRunning   ? 'bg-purple-50 border-purple-200' :
          isLocked    ? 'bg-gray-50 border-gray-200 opacity-60' :
                        'bg-white border-gray-200',
        ].join(' ')}
      >
        <h3 id={titleId} className="text-sm font-bold text-gray-900 leading-tight min-h-[2.625rem]">{stage.title}</h3>
        <StatusBadge status={state.status} />

        <button
          onClick={onRun}
          disabled={disabled}
          className={[
            'w-full flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-semibold text-white transition-opacity',
            disabled ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-90',
          ].join(' ')}
          style={{ background: 'linear-gradient(135deg, #A100FF 0%, #7900B8 100%)' }}
        >
          {isRunning ? (
            <>
              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Running…
            </>
          ) : isLocked ? (
            <>
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Locked
            </>
          ) : (
            <>
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5,3 19,12 5,21" />
              </svg>
              {isCompleted ? 'Re-run Stage' : 'Run Stage'}
            </>
          )}
        </button>

        {isRunning && !isRunAllActive && (
          <button
            onClick={onCancel}
            className="w-full text-xs text-red-500 hover:text-red-700 underline py-0.5"
          >
            Cancel stage
          </button>
        )}

        {/* Supplementary file attachment — hidden while running or locked.
            For idle (non-completed) stages, also hidden while any other stage is running. */}
        {!isRunning && !isLocked && (isCompleted || !isAnyStageRunning) && (
          <div className="flex flex-col gap-1">
            {supplementaryFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 rounded px-2 py-1">
                <FileIcon ext={f.name.split('.').pop() ?? ''} />
                <span className="truncate flex-1">{f.name}</span>
                <button onClick={() => onRemoveFile(i)} className="text-gray-400 hover:text-red-500 flex-shrink-0">✕</button>
              </div>
            ))}
            <label className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 cursor-pointer py-0.5">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              Add context doc
              <input
                type="file"
                accept=".pdf,.docx,.pptx,.xlsx,.xls"
                multiple
                className="hidden"
                onChange={(e) => {
                  const picked = Array.from(e.target.files ?? [])
                  if (!picked.length) return
                  void Promise.all(
                    picked.map((file) => new Promise<{ base64: string; name: string }>((resolve, reject) => {
                      const reader = new FileReader()
                      reader.onload = () => resolve({ base64: (reader.result as string).split(',')[1], name: file.name })
                      reader.onerror = reject
                      reader.readAsDataURL(file)
                    }))
                  ).then(onAttachFiles)
                  e.target.value = ''
                }}
              />
            </label>

            {isCompleted && (
              <div className="mt-1 flex flex-col gap-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Outputs</p>
                {(['xlsx', 'pdf'] as const).map((fmt) => {
                  const filename = `${safeClient}_stage${stage.id}.${fmt}`
                  return (
                    <div key={fmt} className="flex items-center gap-1.5">
                      <FileIcon ext={fmt} />
                      <span className="truncate flex-1 text-xs text-gray-600">{filename}</span>
                      <button
                        onClick={() => onDownloadOutput(fmt, filename)}
                        className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                        title={`Download ${fmt.toUpperCase()}`}
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7,10 12,15 17,10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {isFailed && state.error && (
          <p className="text-xs text-red-500 leading-snug">{state.error}</p>
        )}
      </div>

      {!isLast && (
        <div className={`flex items-center px-2 flex-shrink-0 transition-colors ${isCompleted ? 'text-green-400' : 'text-gray-300'}`}>
          <span className="text-lg">→</span>
        </div>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PipelineWorkflow({
  projectId,
  clientName = 'Client',
  industry = '',
  functionScope = [],
  techStack,
  variant,
  inputFiles,
  branding,
  mmsId,
  purpose,
  onDone,
  onEditDocuments,
  onUpdateDocuments,
  onCancel,
}: PipelineWorkflowProps) {
  const api = useApi()
  const { trackJob, activeJobs, cancelJob } = useGenerationContext()
  const { industries: availableIndustries } = useDropdownOptions()

  const [stages, setStages] = useState<LocalStageState[]>(makeInitialStages)
  const [stageSupplementaryFiles, setStageSupplementaryFiles] = useState<Record<number, Array<{ base64: string; name: string }>>>({})
  const [jobId, setJobId] = useState<string | null>(null)
  const [isInitiating, setIsInitiating] = useState(false)
  const [runError, setRunError] = useState<string | null>(null)
  const [confirmingUpdate, setConfirmingUpdate] = useState(false)
  const [confirmingFullCancel, setConfirmingFullCancel] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [settings, setSettings] = useState<Settings | null>(null)
  const [hasDashboard, setHasDashboard] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [supplementaryWarning, setSupplementaryWarning] = useState(false)
  const [rerunWarning, setRerunWarning] = useState<{ stageIndex: number; downstream: number[] } | null>(null)
  const stageAbortControllers = useRef<Map<number, AbortController>>(new Map())
  const syncToken = useRef(0)
  const mounted = useRef(true)
  const runAllLive = useRef(false)

  // Unmount is enough to drop in-flight syncs: a remount starts its own sync, which supersedes older tokens.
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    api.getSettings().then(setSettings).catch(() => {})
  }, [api])

  // Check on mount whether this project already has a generated dashboard.
  useEffect(() => {
    api.getProject(projectId).then((project) => {
      if (project?.data) setHasDashboard(true)
    }).catch(() => {})
  }, [api, projectId])

  // Card status derives from stored outputs; stale syncs and cards owned by a live run are left alone.
  const syncFromStorage = useCallback(async (failed: Record<number, string> = {}): Promise<void> => {
    const token = ++syncToken.current
    try {
      const outputs = await api.getStageOutputs(projectId)
      if (!mounted.current || token !== syncToken.current) return
      const storedKeys = new Set(Object.entries(outputs).filter(([, v]) => v !== undefined).map(([k]) => k))
      setStages((prev) => prev.map((s, i) => {
        const n = i + 1
        if (s.status === 'running' && (runAllLive.current || stageAbortControllers.current.has(n))) return s
        const failedError = failed[n]
        if (failedError !== undefined) return { status: 'failed' as const, error: failedError }
        const stored = storedKeys.has(`stage${n}`)
        return stored ? { status: 'completed' as const, error: undefined } : { status: 'idle' as const, error: undefined }
      }))
    } catch { /* keep current card state if storage cannot be read */ }
  }, [api, projectId])

  // Hydrate card status from persisted storage on mount (stages 1-6).
  useEffect(() => {
    void syncFromStorage()
  }, [syncFromStorage])

  // Reconnect to an in-progress job for this project after navigation/remount.
  // jobId is local state and resets on unmount; activeJobs persists in context.
  useEffect(() => {
    if (jobId) return
    const existing = activeJobs.find(
      (j) => j.projectId === projectId && j.type === 'inside-out' &&
             (j.status === 'pending' || j.status === 'running')
    )
    if (existing) setJobId(existing.jobId)
  }, [activeJobs, projectId, jobId])

  const trackedJob = jobId ? activeJobs.find((j) => j.jobId === jobId) : undefined
  const isRunning = !!trackedJob && (trackedJob.status === 'pending' || trackedJob.status === 'running')
  // Declared before the progress effect below so the ref is current when that effect triggers a sync.
  useEffect(() => {
    runAllLive.current = isRunning
  }, [isRunning])
  const isStageRunning = stages.some((s) => s.status === 'running')

  // Keepalive: prevents ESO session from expiring during long generation runs.
  // For "Run All", GenerationContext also dispatches tn:generation-keepalive every 3 s.
  // For "Run Stage" (synchronous fetch, no tracked job), this is the only keepalive path,
  // so we must dispatch the event here too — otherwise the ESO inactivity timer and
  // server-side session both go unrefreshed for the duration of a long stage call.
  useEffect(() => {
    if (!isRunning && !isStageRunning) return
    const id = setInterval(() => {
      api.keepAlive?.()
      window.dispatchEvent(new CustomEvent('tn:generation-keepalive'))
    }, 3 * 60 * 1000)
    return () => clearInterval(id)
  }, [api, isRunning, isStageRunning])
  const completedCount = stages.filter((s) => s.status === 'completed').length
  const progress = Math.round((completedCount / STAGES.length) * 100)
  const allDone = completedCount === STAGES.length

  // Content-based key: changes whenever any stage's status transitions (e.g. generating→done),
  // not just when a new event is pushed. withProgress replaces events in-place so
  // progress.length alone would miss generating→done transitions.
  const progressKey = trackedJob?.progress.map((e) => `${e.index}:${e.status}`).join(',') ?? ''

  // Map job progress events to stage card statuses
  useEffect(() => {
    if (!trackedJob) return

    setStages((prev) => {
      const updated = prev.map((s) => ({ ...s }))
      for (const event of trackedJob.progress) {
        const idx = event.index - 1  // event.index is 1-6; array index is 0-5
        if (idx < 0 || idx >= updated.length) continue
        if (event.status === 'generating') updated[idx] = { status: 'running', error: undefined }
        else if (event.status === 'done') updated[idx] = { status: 'completed', error: undefined }
        else if (event.status === 'error') updated[idx] = { status: 'failed', error: event.error }
      }
      if (trackedJob.status === 'failed') {
        const cancelled = trackedJob.error === CANCELLED_MESSAGE
        return updated.map((s) => s.status !== 'running' ? s
          : cancelled ? { status: 'idle' as const, error: undefined }
          : { status: 'failed' as const, error: trackedJob.error ?? 'Stage failed.' })
      }
      return updated
    })

    if (trackedJob.status === 'succeeded') {
      setJobId(null)
      setHasDashboard(true)
      void syncFromStorage()
    } else if (trackedJob.status === 'failed') {
      setJobId(null)
      const failedStages: Record<number, string> = {}
      for (const event of trackedJob.progress) {
        if (event.status === 'error') failedStages[event.index] = event.error ?? 'Stage failed.'
      }
      void syncFromStorage(failedStages)
    }
    // Keyed on progressKey + status rather than trackedJob: activeJobs yields a new object on every
    // poll, so depending on trackedJob would re-run this (and syncFromStorage) on each tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressKey, trackedJob?.status, syncFromStorage])

  async function handleRunAll() {
    if (!settings || isRunning || isInitiating || isStageRunning) return

    const apiKey =
      settings.aiProvider === 'claude' ? settings.anthropicApiKey :
      settings.aiProvider === 'azure-openai' ? settings.azureApiKey :
      settings.openaiApiKey

    const files = (inputFiles ?? [])
      .filter((f): f is { name: string; base64: string } => typeof f.base64 === 'string' && f.base64.length > 0)
      .map(({ base64, name }) => ({ base64, name }))

    if (files.length === 0) {
      setRunError('No files available — please go back and re-upload the documents.')
      return
    }

    if (Object.values(stageSupplementaryFiles).some((f) => f.length > 0)) {
      setSupplementaryWarning(true)
    }
    setRunError(null)
    setIsInitiating(true)
    syncToken.current++

    const payload: GenerateInsideOutPayload = {
      projectId,
      files,
      clientName,
      industry,
      functionScope,
      techStack,
      variant,
      provider: settings.aiProvider,
      apiKey,
      branding,
      mmsId,
      purpose,
      industryChoices: industry ? undefined : availableIndustries,
    }

    try {
      const result = await api.generateInsideOut(payload)
      if (result.error) {
        setRunError(result.error)
        setIsInitiating(false)
        return
      }
      trackJob(result.jobId, projectId, 'inside-out', clientName, branding.primaryColor)
      setJobId(result.jobId)
      setIsInitiating(false)
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Failed to start analysis.')
      setIsInitiating(false)
    }
  }

  async function handleRunStage(stageIndex: 1 | 2 | 3 | 4 | 5 | 6) {
    if (!settings || isRunning || isInitiating) return
    const idx = stageIndex - 1
    if (stages[idx].status === 'running') return

    const apiKey =
      settings.aiProvider === 'claude' ? settings.anthropicApiKey :
      settings.aiProvider === 'azure-openai' ? settings.azureApiKey :
      settings.openaiApiKey

    const files = (inputFiles ?? [])
      .filter((f): f is { name: string; base64: string } => typeof f.base64 === 'string' && f.base64.length > 0)
      .map(({ base64, name }) => ({ base64, name }))

    if (stageIndex <= 2 && files.length === 0) {
      setRunError('No files available — please go back and re-upload the documents.')
      return
    }

    const abortController = new AbortController()
    stageAbortControllers.current.set(stageIndex, abortController)
    syncToken.current++

    setRunError(null)
    const dependents = getTransitiveDependents(stageIndex)
    if (hasDashboard && stages[idx].status === 'completed' && dependents.length > 0) {
      setRerunWarning({ stageIndex, downstream: dependents })
    }
    // Dependents are deleted server-side only on success; show them idle and re-sync on failure or cancel.
    setStages((prev) => {
      const updated = prev.map((s, i) => i === idx ? { status: 'running' as const, error: undefined } : s)
      dependents.forEach((d) => { updated[d - 1] = { status: 'idle', error: undefined } })
      return updated
    })

    const payload: RunInsideOutStagePayload = {
      projectId,
      stageIndex,
      files,
      supplementaryFiles: stageSupplementaryFiles[stageIndex] ?? [],
      clientName,
      industry,
      functionScope,
      techStack,
      variant,
      provider: settings.aiProvider,
      apiKey,
    }

    const releaseController = () => {
      if (stageAbortControllers.current.get(stageIndex) === abortController) {
        stageAbortControllers.current.delete(stageIndex)
      }
    }

    try {
      const res = await api.runInsideOutStage(payload, abortController.signal)

      // A cancelled run may still have settled in the main process (desktop); re-sync from storage.
      if (abortController.signal.aborted) {
        releaseController()
        setRerunWarning(null)
        await syncFromStorage()
        return
      }

      releaseController()
      if (res.error || !res.result) {
        setRerunWarning(null)
        const message = res.error ?? 'Stage returned no result.'
        setStages((prev) => prev.map((s, i) => i === idx ? { status: 'failed' as const, error: message } : s))
        await syncFromStorage({ [stageIndex]: message })
        return
      }

      setStages((prev) => prev.map((s, i) => i === idx ? { status: 'completed' as const, error: undefined } : s))
      await syncFromStorage()

      // Mirror Run All: persist project.data as soon as stage 6 finishes so the
      // dashboard is available even if the user navigates away before clicking the button.
      if (stageIndex === 6) {
        try {
          const finalizeRes = await api.finalizeInsideOut(projectId)
          if (finalizeRes.error) setRunError(finalizeRes.error)
          else setHasDashboard(true)
        } catch { /* non-fatal — user can still finalize via the button */ }
      }
    } catch (err) {
      releaseController()
      setRerunWarning(null)
      if (abortController.signal.aborted) {
        await syncFromStorage()
        return
      }
      const message = err instanceof Error ? err.message : 'Stage failed.'
      setStages((prev) => prev.map((s, i) => i === idx ? { status: 'failed' as const, error: message } : s))
      await syncFromStorage({ [stageIndex]: message })
    } finally {
      releaseController()
    }
  }

  function handleCancelStage(stageIndex: 1 | 2 | 3 | 4 | 5 | 6) {
    stageAbortControllers.current.get(stageIndex)?.abort()
    stageAbortControllers.current.delete(stageIndex)
    setStages((prev) => prev.map((s, i) => i === stageIndex - 1 ? { status: 'idle', error: undefined } : s))
    setRerunWarning(null)
    void syncFromStorage()
  }

  // A stage is unlocked when every direct prerequisite is completed (shared dependency map).
  function isStageUnlocked(stageIndex: StageIndex): boolean {
    const deps = STAGE_DEPENDENCIES[stageIndex]
    return deps.every((dep) => stages[dep - 1].status === 'completed')
  }

  // Returns whether the Run Stage button should be interactive (1-based stageIndex).
  // A job running disables all buttons but does NOT visually lock unlocked stages.
  function isStageRunnable(stageIndex: StageIndex): boolean {
    if (isRunning || isInitiating || isStageRunning) return false
    if (stages[stageIndex - 1].status === 'running') return false
    return isStageUnlocked(stageIndex)
  }


  function handleUpdateDocuments() {
    if (onEditDocuments) {
      setShowEditModal(true)
    } else if (onUpdateDocuments) {
      setConfirmingUpdate(true)
    }
  }

  function handleConfirmUpdate() {
    handleReset()
    setConfirmingUpdate(false)
    onUpdateDocuments?.()
  }

  async function handleConfirmFullCancel() {
    if (jobId) await cancelJob(jobId)
    stageAbortControllers.current.forEach((ctrl) => ctrl.abort())
    stageAbortControllers.current.clear()
    setConfirmingFullCancel(false)
    onCancel?.()
  }

  function handleReset() {
    if (jobId) void cancelJob(jobId)
    setJobId(null)
    setRunError(null)
    setHasDashboard(false)
    setRerunWarning(null)
    setSupplementaryWarning(false)
    setStages(makeInitialStages())
    setStageSupplementaryFiles({})
    // Clear persisted precomputed on desktop so hydration doesn't restore stale results.
    void api.getProject(projectId).then((project) => {
      if (project?.precomputed) {
        void api.saveProject({ ...project, precomputed: null, updatedAt: new Date().toISOString() })
      }
    }).catch(() => {})
  }

  async function handleDownloadOutput(stageIndex: number, format: 'xlsx' | 'pdf', filename: string) {
    try {
      await api.downloadStageOutput?.(projectId, stageIndex, format, filename)
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Download failed.')
    }
  }

  function handleAttachFiles(stageIndex: number, newFiles: Array<{ base64: string; name: string }>) {
    setStageSupplementaryFiles((prev) => ({
      ...prev,
      [stageIndex]: [...(prev[stageIndex] ?? []), ...newFiles],
    }))
  }

  function handleRemoveSupplementaryFile(stageIndex: number, fileIndex: number) {
    setStageSupplementaryFiles((prev) => {
      const current = prev[stageIndex] ?? []
      const updated = current.filter((_, i) => i !== fileIndex)
      return { ...prev, [stageIndex]: updated }
    })
  }

  const displayFiles = inputFiles?.length
    ? inputFiles
    : [{ name: `${clientName}_Input_Template.xlsx` }]

  // Hide the Edit Documents button only while generation is actively in-progress;
  // completed stages should not prevent mid-session editing (that is the point of this feature).
  const isGenerating = isInitiating || isRunning || isStageRunning

  return (
    <div className="flex flex-col gap-4 bg-white rounded-xl border border-gray-200 p-4 shadow-sm w-full">

      {showEditModal && onEditDocuments && (
        <EditDocumentsModal
          files={(inputFiles ?? []).filter((f): f is { base64: string; name: string } => typeof f.base64 === 'string')}
          isRunning={isGenerating}
          onConfirm={(updated) => { setShowEditModal(false); onEditDocuments(updated) }}
          onReset={() => { setShowEditModal(false); handleConfirmUpdate() }}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* ── Input Files ──────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold tracking-widest text-gray-500 uppercase">Input Files</span>
          {(onEditDocuments || onUpdateDocuments) && !isGenerating && (
            confirmingUpdate ? (
              <span className="flex items-center gap-2">
                {/* Only reachable while nothing is generating (see the !isGenerating guard above). */}
                <span className="text-xs text-gray-600">
                  Editing documents will reset current stage progress.
                </span>
                <button
                  onClick={handleConfirmUpdate}
                  className="px-3 py-1 bg-red-500 text-white rounded text-xs font-semibold hover:bg-red-600 transition-colors"
                >
                  Yes, edit
                </button>
                <button
                  onClick={() => setConfirmingUpdate(false)}
                  className="text-xs text-gray-500 underline hover:text-gray-700"
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                onClick={handleUpdateDocuments}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit Documents
              </button>
            )
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {displayFiles.map((f) => {
            const ext = f.name.split('.').pop() ?? ''
            return (
              <div key={f.name} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50">
                <FileIcon ext={ext} />
                <span className="text-sm text-gray-700">{f.name}</span>
              </div>
            )
          })}
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* ── Navigate-away banner ─────────────────────────────────────── */}
      {isRunning && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
          <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <span className="flex-1">
            <strong>Analysis running in the background</strong> — you may navigate away safely and monitor the progress in My Talent Navigators page.
          </span>
        </div>
      )}

      {supplementaryWarning && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <svg className="w-4 h-4 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span className="flex-1">Run All ignores per-stage context docs. Use "Run Stage" individually to apply them.</span>
          <button onClick={() => setSupplementaryWarning(false)} className="flex-shrink-0 text-amber-600 hover:text-amber-800 text-lg leading-none">×</button>
        </div>
      )}

      {/* ── Re-run warning ───────────────────────────────────────────── */}
      {rerunWarning && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <svg className="w-4 h-4 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span className="flex-1">
            Re-running Stage {rerunWarning.stageIndex} will reset {formatStageList(rerunWarning.downstream)}. Re-run them to update the navigator.
          </span>
          <button onClick={() => setRerunWarning(null)} className="flex-shrink-0 text-amber-600 hover:text-amber-800 text-lg leading-none">×</button>
        </div>
      )}

      {/* ── Processing Stages ────────────────────────────────────────── */}
      <section>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-bold tracking-widest text-gray-500 uppercase">Processing Stages</span>

          <button
            onClick={() => void handleRunAll()}
            disabled={!settings || isInitiating || isRunning || isStageRunning || allDone}
            className={[
              'px-4 py-1.5 text-xs font-semibold rounded border transition-colors',
              !settings || isInitiating || isRunning || isStageRunning || allDone
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                : 'border-purple-400 bg-white hover:bg-purple-50 text-purple-700',
            ].join(' ')}
          >
            {isInitiating ? (
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                Starting…
              </span>
            ) : isRunning ? (
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                Running…
              </span>
            ) : (
              'Run All Stages'
            )}
          </button>

          <button
            onClick={handleReset}
            disabled={isRunning}
            className="p-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Reset all stages"
          >
            <RefreshIcon />
          </button>

          <span className="text-xs text-gray-400">
            {allDone ? 'All stages complete' : `${completedCount} / ${STAGES.length} stages done`}
          </span>
        </div>

        <div className="overflow-x-auto pb-3">
          <div className="flex items-stretch gap-0 min-w-max">
            {STAGES.map((stage, idx) => (
              <StageCard
                key={stage.id}
                stage={stage}
                state={stages[idx]}
                isLast={idx === STAGES.length - 1}
                isLocked={!isStageUnlocked(stage.id)}
                isDisabled={!isStageRunnable(stage.id)}
                isAnyStageRunning={isRunning || isStageRunning}
                isRunAllActive={jobId !== null}
                supplementaryFiles={stageSupplementaryFiles[stage.id] ?? []}
                clientName={clientName}
                onRun={() => void handleRunStage(stage.id)}
                onCancel={() => handleCancelStage(stage.id)}
                onAttachFiles={(f) => handleAttachFiles(stage.id, f)}
                onRemoveFile={(i) => handleRemoveSupplementaryFile(stage.id, i)}
                onDownloadOutput={(fmt, filename) => void handleDownloadOutput(stage.id, fmt, filename)}
              />
            ))}
          </div>
        </div>

        <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #A100FF 0%, #7900B8 100%)' }}
          />
        </div>
        <div className="mt-1 flex items-center justify-between">
          <div>
            {onCancel && (isRunning || isStageRunning) && (
              confirmingFullCancel ? (
                <span className="flex items-center gap-2">
                  <span className="text-xs text-gray-600">Cancel and discard progress?</span>
                  <button
                    onClick={handleConfirmFullCancel}
                    className="px-3 py-1 bg-red-500 text-white rounded text-xs font-semibold hover:bg-red-600 transition-colors"
                  >
                    Yes, cancel
                  </button>
                  <button
                    onClick={() => setConfirmingFullCancel(false)}
                    className="text-xs text-gray-500 underline hover:text-gray-700"
                  >
                    Keep running
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => setConfirmingFullCancel(true)}
                  className="text-xs text-red-500 underline hover:text-red-700 transition-colors"
                >
                  Cancel generation
                </button>
              )
            )}
          </div>
          <p className="text-xs text-gray-400">
            {completedCount} / {STAGES.length} stages completed
          </p>
        </div>
      </section>

      {/* ── Error ────────────────────────────────────────────────────── */}
      {runError && (
        <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          ✗ {runError}
        </div>
      )}

      {/* ── Submit ───────────────────────────────────────────────────── */}
      {allDone && (
        <>
          <hr className="border-gray-100" />
          <section className="flex items-center justify-between gap-4">
            <p className="text-sm font-semibold text-gray-800">All stages complete — navigator is ready.</p>
            <button
              onClick={async () => {
                setIsFinalizing(true)
                const res = await api.finalizeInsideOut(projectId).catch((e) => ({ error: e instanceof Error ? e.message : String(e) }))
                setIsFinalizing(false)
                if (res.error) { setRunError(res.error); return }
                setHasDashboard(true)
                onDone?.()
              }}
              disabled={isFinalizing}
              className="px-8 py-2.5 rounded-lg text-sm font-bold text-white hover:opacity-90 transition-opacity whitespace-nowrap disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #A100FF 0%, #7900B8 100%)' }}
            >
              {isFinalizing ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Building navigator…
                </span>
              ) : 'Open Navigator →'}
            </button>
          </section>
        </>
      )}

    </div>
  )
}
